import React, { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Dropdown, { DropdownItem, DropdownMenu, DropdownToggle } from './bootstrap/Dropdown';
import Icon from './icon/Icon';
import LANG, { getLangWithKey, ILang, SELECTABLE_LANG_KEYS } from '../lang';
import './LanguageSelector.css';

/**
 * Header language picker. Deliberately flag-free: a flag names a country, not a
 * language (Arabic, English and Greek are each spoken across several states),
 * so the toggle shows a globe + short code and the menu lists native names.
 */
const LanguageSelector = () => {
	const { t, i18n } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const current = getLangWithKey(i18n.language as ILang['key']['lng']) || LANG.EN;

	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', i18n.language.substring(0, 2));
	});

	const choose = (lng: ILang['key']['lng']) => {
		setIsOpen(false);
		if (lng !== current.lng) i18n.changeLanguage(lng).then();
	};

	return (
		<Dropdown isOpen={isOpen} setIsOpen={setIsOpen}>
			<DropdownToggle hasIcon={false}>
				<button
					type='button'
					className={classNames('lang-pill', { 'lang-pill--open': isOpen })}
					aria-label={`${t('Language')}: ${current.text}`}
					aria-haspopup='listbox'
					aria-expanded={isOpen}
					data-tour='lang-selector'>
					<Icon icon='Language' className='lang-pill__globe' />
					<span className='lang-pill__code'>{current.code}</span>
					<Icon icon='ExpandMore' className='lang-pill__chevron' />
				</button>
			</DropdownToggle>
			<DropdownMenu isAlignmentEnd className='lang-menu' data-tour='lang-selector-menu'>
				<DropdownItem isHeader>
					<span className='lang-menu__title'>{t('Language')}</span>
				</DropdownItem>
				{SELECTABLE_LANG_KEYS.map((k) => {
					const lang = LANG[k];
					const active = lang.lng === current.lng;
					return (
						<DropdownItem key={lang.lng}>
							<button
								type='button'
								role='option'
								aria-selected={active}
								lang={lang.lng === 'gr' ? 'el' : lang.lng}
								className={classNames('lang-option', { 'lang-option--active': active })}
								onClick={() => choose(lang.lng)}>
								<span className='lang-option__code'>{lang.code}</span>
								<span className='lang-option__names'>
									<span className='lang-option__native'>{lang.text}</span>
									{lang.englishName !== lang.text && (
										<span className='lang-option__english'>{lang.englishName}</span>
									)}
								</span>
								{active && <Icon icon='Check' className='lang-option__check' />}
							</button>
						</DropdownItem>
					);
				})}
			</DropdownMenu>
		</Dropdown>
	);
};

export default LanguageSelector;
